import { Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { getErrorMessage } from '@/api/errors'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteVariableGroup } from '@/hooks'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { VariableGroupSummary } from '@/types'

interface DeleteVariableGroupDialogProps {
  projectId: string
  group: VariableGroupSummary
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function DeleteVariableGroupDialog({ projectId, group, open, onOpenChange }: DeleteVariableGroupDialogProps) {
  const deleteGroup = useDeleteVariableGroup(projectId)

  const handleDelete = async () => {
    try {
      await deleteGroup.mutateAsync(group.id)
      toast.success(`Environment variable group "${group.name}" deleted.`)
      onOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to delete the environment variable group.')
    }
  }

  const variablesLine = group.variableCount === 1
    ? 'Its 1 environment variable is removed, and secret values are purged permanently.'
    : `Its ${group.variableCount} environment variables are removed, and secret values are purged permanently.`

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            Delete Environment Variable Group
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none" aria-label="Close">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription asChild>
          <div className="space-y-3 text-sm text-foreground">
            <p>
              Delete <span className="font-bold">{group.name}</span>?
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li>{variablesLine}</li>
              <li>Workflows will no longer be able to use this group.</li>
              <li>Usage history will be kept.</li>
              <li>Runs already in progress will keep working until they finish.</li>
            </ul>
          </div>
        </DialogDescription>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-10 rounded-lg px-4 font-bold">
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteGroup.isPending}
            className="h-10 rounded-lg px-4 font-bold"
          >
            <Trash2 className="h-5 w-5 mr-1" />
            {deleteGroup.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
